import { computed } from 'vue'

import { defineStore } from 'pinia'

import { useStorage } from '@vueuse/core'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { utcVal } from '@tsyl/radon'

import useAuthStore from '@/stores/auth.ts'
import type { UserMetadata } from '@/types/User.ts'
// import { editUser } from "@/services/api/user.ts"
// import AuthStore from "@/stores/auth.ts"

const language = import.meta.env.VITE_DEFAULT_LANG as string
const defaultTheme = import.meta.env.VITE_DEFAULT_THEME as string
const defaultDateFormat = import.meta.env.VITE_DEFAULT_DATE_FORMAT as string
const defaultTimeFormat = import.meta.env.VITE_DEFAULT_TIME_FORMAT as string
const defaultTimeZone = import.meta.env.VITE_TIMEZONE as string
const defaultMetricUnits = import.meta.env.VITE_DEFAULT_METRIC_UNITS as string
const defaultCoordinateUnits = import.meta.env.VITE_DEFAULT_COORDINATE_UNITS as string

export default defineStore('settings', () => {
  const authStore = useAuthStore()
  const lang = useStorage('lang', language as string)
  const theme = useStorage('theme', defaultTheme as string)
  const dateFormat = useStorage('dateFormat', defaultDateFormat as string)
  const timeFormat = useStorage('timeFormat', defaultTimeFormat as string)
  const timezone = useStorage('timezone', defaultTimeZone as string)
  const metricUnits = useStorage('metricUnits', defaultMetricUnits as string)
  const coordinateUnits = useStorage('coordinateUnits', defaultCoordinateUnits as string)
  // A tenant can fix its own date format; it wins over the user preference so every date of the tenant reads the same
  const tenantDateFormat = computed(() => authStore.org?.dateFormat)
  const effectiveDateFormat = computed(() => tenantDateFormat.value ?? dateFormat.value)
  // const { locale: i18nLocale } = useI18n({ useScope: "global" })
  // const { current } = useLocale()

  function updateSettings(userAttributes: UserMetadata) {
    lang.value = userAttributes.Language ?? lang.value
    theme.value = userAttributes.Theme ?? theme.value
    dateFormat.value = userAttributes.DateFormat ?? dateFormat.value
    timeFormat.value = userAttributes.DateFormat ?? timeFormat.value
    timezone.value = userAttributes.Timezone ?? timezone.value
    metricUnits.value = userAttributes.Units ?? metricUnits.value
    coordinateUnits.value = userAttributes.Coordinates ?? coordinateUnits.value
    // i18nLocale.value = lang.value
    // current.value = lang.value
  }

  function formatStringDate(date: string, chosenTimezone: string, chosenFormat: string): string {
    return formatStringDateCommon(date, chosenTimezone, chosenFormat)
  }

  function formatStringDateTime(
    date: string,
    chosenTimezone: string = timezone.value,
    chosenFormat = `${effectiveDateFormat.value} HH:mm`
  ): string {
    return formatStringDateCommon(date, chosenTimezone, chosenFormat)
  }

  // Label of a forecast column (a calendar day with no timezone): the tenant format when it has one, otherwise the short MM/dd it always had
  function formatForecastDay(date: Date): string {
    return format(date, tenantDateFormat.value ?? 'MM/dd')
  }

  function getTimeExtent(date: Date) {
    return `${formatStringDateCommon(date)}/${formatStringDateCommon(new Date())}`
  }

  function formatStringDateCommon(
    date: string | Date,
    chosenTimezone: string = timezone.value,
    chosenFormat: string = effectiveDateFormat.value
  ) {
    if (!date) return '-'

    try {
      return format(toZonedTime(typeof date === 'string' ? utcVal(date) : date, chosenTimezone), chosenFormat)
    } catch (ex) {
      console.error(`Error formatting date. Date: ${date} Timezone: ${chosenTimezone} Format: ${chosenFormat}`)
      return ''
    }
  }

  return {
    lang,
    theme,
    dateFormat,
    effectiveDateFormat,
    tenantDateFormat,
    timeFormat,
    timezone,
    metricUnits,
    coordinateUnits,
    updateSettings,
    formatStringDate,
    formatStringDateTime,
    formatForecastDay,
    getTimeExtent
  }
})
